/**
 * @file index.tsx
 *
 * @fileoverview Renders selected job detail or job detail
 * instructions if none job is selected.
 */

import dynamic from 'next/dynamic';
import { Job } from '~/jobiqo/types/types';
import { PAGE_TYPES, PageType } from '~/jobiqo/lib/analytics';
import { User } from '~/jobiqo/models/user';
import JobSearchEmptySlot from '~/jobiqo/components/Slots/JobSearchEmptySlot';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';

const JobDetail = dynamic(import('~/jobiqo/components/Job/Detail'));

interface JobDetailPanelProps {
  /**
   * The current job.
   */
  currentJob: Job | null;
  /**
   * The current user.
   */
  user?: User;
  /**
   * The children element.
   */
  children: React.ReactNode;
  /**
   * The count of jobs.
   */
  totalCount?: number;
  /**
   * If external anchors in the main job description should get rel=nofollow.
   */
  noFollowExternalLinks?: boolean;
  /**
   * If external anchors in the main job description should be rendered as plain text.
   */
  plainTextExternalLinks?: boolean;
  /**
   * Disable the standalone cover-image overlap margin.
   */
  disableCoverOverlap?: boolean;
  /**
   * Hide breadcrumbs above the job detail.
   */
  hideBreadcrumbs?: boolean;
  /**
   * Analytics page context for detail events in split view.
   */
  pageType?: PageType;
}

export const JobDetailPanel = ({
  currentJob,
  user,
  children,
  totalCount,
  noFollowExternalLinks = false,
  plainTextExternalLinks = false,
  disableCoverOverlap = false,
  hideBreadcrumbs = false,
  pageType = PAGE_TYPES.JOB_SEARCH,
}: JobDetailPanelProps) =>
  currentJob ? (
    <JobDetail
      fullContainer={true}
      overflow={true}
      user={user}
      job={currentJob}
      noFollowExternalLinks={noFollowExternalLinks}
      plainTextExternalLinks={plainTextExternalLinks}
      disableCoverOverlap={disableCoverOverlap}
      hideBreadcrumbs={hideBreadcrumbs}
      pageType={pageType}
    >
      {children}
    </JobDetail>
  ) : (
    <HideMobile>
      <Spacing size="large" />
      <JobSearchEmptySlot totalCount={totalCount} />
    </HideMobile>
  );

export default JobDetailPanel;
